import React, { useState, useEffect } from 'react';
import { User, LogOut, Award, Zap, Heart, Leaf, TrendingUp, Users, Server, Laptop, Recycle, Cloud, BookOpen, Shield, Settings, Home, BarChart3, Trophy, Info, Mail, Lock, School, Globe, Cpu, Download, CheckCircle, Star, Award as AwardIcon, GitBranch, FileText, Database, Smartphone, Wifi, Code, Coffee } from 'lucide-react';

const NirdBuilderApp = () => {
  const [currentPage, setCurrentPage] = useState('login');
  const [user, setUser] = useState(null);
  const [schoolState, setSchoolState] = useState({
    name: 'Lycée Carnot de Bruay-la-Buissière',
    level: 1,
    points: 0,
    inclusion: 30,
    responsibility: 25,
    sustainability: 20,
    actions: [],
    members: 12
  });

  const [leaderboard, setLeaderboard] = useState([
    { rank: 1, school: 'Lycée Carnot', points: 2450, level: 8, region: 'Hauts-de-France' },
    { rank: 2, school: 'Collège Victor Hugo', points: 1890, level: 6, region: 'Île-de-France' },
    { rank: 3, school: 'École Jean Moulin', points: 1620, level: 5, region: 'Auvergne-Rhône-Alpes' },
    { rank: 4, school: 'Lycée Louise Michel', points: 1480, level: 5, region: 'Occitanie' },
    { rank: 5, school: 'Collège Albert Einstein', points: 1320, level: 4, region: 'Provence-Alpes-Côte d\'Azur' }
  ]);

  const nirdActions = [
    {
      id: 1,
      title: 'Installer Linux sur 10 PC',
      desc: 'Donnez une seconde vie aux ordinateurs avec un OS libre et luttez contre l\'obsolescence programmée',
      icon: Cpu,
      cost: 100,
      rewards: { inclusion: 15, responsibility: 10, sustainability: 25, points: 150 },
      duration: '2 semaines',
      difficulty: 'Moyen',
      category: 'Linux & Logiciels Libres'
    },
    {
      id: 2,
      title: 'Créer un cloud local NextCloud',
      desc: 'Hébergez vos données dans l\'établissement avec une solution libre et souveraine',
      icon: Cloud,
      cost: 200,
      rewards: { inclusion: 10, responsibility: 25, sustainability: 15, points: 250 },
      duration: '1 mois',
      difficulty: 'Difficile',
      category: 'Souveraineté Numérique'
    },
    {
      id: 3,
      title: 'Organiser un repair café numérique',
      desc: 'Atelier de réparation avec les élèves pour promouvoir le réemploi du matériel',
      icon: Recycle,
      cost: 50,
      rewards: { inclusion: 20, responsibility: 15, sustainability: 30, points: 120 },
      duration: '1 semaine',
      difficulty: 'Facile',
      category: 'Économie Circulaire'
    },
    {
      id: 4,
      title: 'Migration vers LibreOffice',
      desc: 'Remplacez la suite bureautique propriétaire par une alternative libre',
      icon: FileText,
      cost: 80,
      rewards: { inclusion: 15, responsibility: 20, sustainability: 10, points: 100 },
      duration: '2 semaines',
      difficulty: 'Facile',
      category: 'Logiciels Libres'
    },
    {
      id: 5,
      title: 'Installer un serveur de fichiers',
      desc: 'Serveur de partage local et sécurisé pour l\'établissement',
      icon: Server,
      cost: 150,
      rewards: { inclusion: 10, responsibility: 20, sustainability: 15, points: 180 },
      duration: '3 semaines',
      difficulty: 'Moyen',
      category: 'Infrastructure'
    },
    {
      id: 6,
      title: 'Rédiger une charte de sobriété numérique',
      desc: 'Créez une charte avec les éco-délégués pour réduire l\'impact environnemental',
      icon: Leaf,
      cost: 30,
      rewards: { inclusion: 25, responsibility: 15, sustainability: 25, points: 90 },
      duration: '1 semaine',
      difficulty: 'Facile',
      category: 'Sobriété Numérique'
    },
    {
      id: 7,
      title: 'Mettre en place un réseau Wi-Fi libre',
      desc: 'Configuration d\'un réseau Wi-Fi sécurisé avec gestion libre',
      icon: Wifi,
      cost: 120,
      rewards: { inclusion: 20, responsibility: 15, sustainability: 10, points: 140 },
      duration: '2 semaines',
      difficulty: 'Moyen',
      category: 'Infrastructure'
    },
    {
      id: 8,
      title: 'Atelier développement web avec Framasoft',
      desc: 'Initiation aux outils libres de développement collaboratif',
      icon: Code,
      cost: 60,
      rewards: { inclusion: 25, responsibility: 10, sustainability: 5, points: 110 },
      duration: '1 semaine',
      difficulty: 'Facile',
      category: 'Formation'
    }
  ];

  const testimonials = [
    {
      name: 'Marie, enseignante',
      role: 'Professeure de technologie',
      text: 'Grâce à NIRD, nous avons pu réutiliser 20 ordinateurs qui allaient être jetés. Les élèves sont motivés et apprennent concrètement la sobriété numérique.',
      school: 'Lycée Carnot'
    },
    {
      name: 'Karim, technicien',
      role: 'Administrateur réseau',
      text: 'La migration vers Linux nous a fait économiser 15 000€ en licences. Le cloud local nous donne enfin le contrôle sur nos données.',
      school: 'Collège Victor Hugo'
    },
    {
      name: 'Léa, élève',
      role: 'Éco-déléguée',
      text: 'Notre repair café a permis de réparer 35 téléphones et ordinateurs. On se sent acteurs de la transition écologique !',
      school: 'École Jean Moulin'
    }
  ];

  const handleLogin = (e) => {
    e.preventDefault();
    const email = e.target.email.value;
    const password = e.target.password.value;
    
    if (email && password) {
      setUser({ 
        email, 
        name: email.split('@')[0], 
        avatar: '👤',
        role: 'Enseignant'
      });
      setCurrentPage('dashboard');
    }
  };

  const handleSignup = (e) => {
    e.preventDefault();
    const name = e.target.name.value;
    const email = e.target.email.value;
    const school = e.target.school.value;
    const password = e.target.password.value;
    const role = e.target.role.value;
    
    if (name && email && school && password) {
      setUser({ 
        email, 
        name, 
        school, 
        role,
        avatar: '👤' 
      });
      setSchoolState({ ...schoolState, name: school });
      setCurrentPage('dashboard');
    }
  };

  const handleLogout = () => {
    setUser(null);
    setCurrentPage('login');
  };

  const applyAction = (action) => {
    if (schoolState.points >= action.cost) {
      const newPoints = schoolState.points - action.cost + action.rewards.points;
      const newInclusion = Math.min(100, schoolState.inclusion + action.rewards.inclusion);
      const newResponsibility = Math.min(100, schoolState.responsibility + action.rewards.responsibility);
      const newSustainability = Math.min(100, schoolState.sustainability + action.rewards.sustainability);
      const newLevel = Math.floor(newPoints / 500) + 1;
      const newMembers = schoolState.members + Math.floor(Math.random() * 3) + 1;

      setSchoolState({
        ...schoolState,
        points: newPoints,
        level: newLevel,
        inclusion: newInclusion,
        responsibility: newResponsibility,
        sustainability: newSustainability,
        members: newMembers,
        actions: [...schoolState.actions, { ...action, timestamp: Date.now() }]
      });
      
      // Mettre à jour le classement
      const updatedLeaderboard = [...leaderboard];
      const ourSchoolIndex = updatedLeaderboard.findIndex(s => s.school === schoolState.name);
      if (ourSchoolIndex !== -1) {
        updatedLeaderboard[ourSchoolIndex].points = newPoints;
        updatedLeaderboard[ourSchoolIndex].level = newLevel;
        updatedLeaderboard.sort((a, b) => b.points - a.points);
        updatedLeaderboard.forEach((school, index) => school.rank = index + 1);
        setLeaderboard(updatedLeaderboard);
      }
    }
  };

  const LoginPage = () => (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
          <div className="text-center mb-8">
            <div className="inline-block p-3 bg-green-100 rounded-full mb-4">
              <Leaf className="w-10 h-10 text-green-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-800 mb-2">
              <span className="text-green-600">NIRD</span> Builder
            </h1>
            <p className="text-gray-600">Le Village Numérique Résistant</p>
            <p className="text-sm text-gray-500 mt-1">Numérique Inclusif, Responsable et Durable</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Mail className="inline w-4 h-4 mr-1" /> Email
              </label>
              <input
                type="email"
                name="email"
                placeholder="enseignant@education.fr"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none transition"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Lock className="inline w-4 h-4 mr-1" /> Mot de passe
              </label>
              <input
                type="password"
                name="password"
                placeholder="••••••••"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none transition"
                required
              />
            </div>
            <button
              type="submit"
              className="w-full bg-gradient-to-r from-green-600 to-emerald-600 text-white py-3 rounded-lg font-medium hover:from-green-700 hover:to-emerald-700 transition transform hover:scale-[1.02] shadow-md"
            >
              Se connecter au village
            </button>
          </form>

          <div className="mt-6 text-center space-y-3">
            <button
              onClick={() => setCurrentPage('signup')}
              className="text-green-600 hover:text-green-700 font-medium text-sm block w-full"
            >
              Pas encore de compte ? Rejoindre la résistance numérique
            </button>
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">ou</span>
              </div>
            </div>
            <button
              onClick={() => {
                setUser({ 
                  email: 'demo@nird.fr', 
                  name: 'Visiteur NIRD', 
                  avatar: '👤',
                  role: 'Visiteur',
                  school: 'Lycée Carnot de Bruay-la-Buissière'
                });
                setSchoolState({ 
                  ...schoolState, 
                  name: 'Lycée Carnot de Bruay-la-Buissière', 
                  points: 250,
                  members: 15
                });
                setCurrentPage('dashboard');
              }}
              className="w-full bg-gray-100 text-gray-700 py-3 rounded-lg font-medium hover:bg-gray-200 transition shadow-sm"
            >
              🎮 Mode démo - Explorer le village
            </button>
          </div>
        </div>

        <div className="mt-6 text-center text-sm text-gray-600">
          <p>Projet NIRD - Nuit de l'Info 2025</p>
          <p className="mt-1">Porté par le collectif enseignant et la DNE</p>
          <a 
            href="https://nird.forge.apps.education.fr/" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-green-600 hover:text-green-700 underline inline-flex items-center mt-2"
          >
            <Globe className="w-3 h-3 mr-1" /> Site officiel NIRD
          </a>
        </div>
      </div>
    </div>
  );

  const SignupPage = () => (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
          <div className="text-center mb-8">
            <div className="inline-block p-3 bg-green-100 rounded-full mb-4">
              <Users className="w-10 h-10 text-green-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-800 mb-2">Rejoindre <span className="text-green-600">NIRD</span></h1>
            <p className="text-gray-600">Créez votre village numérique résistant</p>
          </div>

          <form onSubmit={handleSignup} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Nom complet</label>
              <input
                type="text"
                name="name"
                placeholder="Jean Dupont"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none transition"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Email professionnel</label>
              <input
                type="email"
                name="email"
                placeholder="votre@education.fr"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none transition"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Établissement</label>
              <input
                type="text"
                name="school"
                placeholder="Lycée Carnot de Bruay-la-Buissière"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none transition"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Rôle</label>
              <select
                name="role"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none transition"
                required
              >
                <option value="">Sélectionnez votre rôle</option>
                <option value="enseignant">Enseignant·e</option>
                <option value="eleve">Élève / Éco-délégué·e</option>
                <option value="technicien">Technicien / Administrateur</option>
                <option value="direction">Direction d'établissement</option>
                <option value="collectivite">Collectivité territoriale</option>
                <option value="parent">Parent d'élève</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Mot de passe</label>
              <input
                type="password"
                name="password"
                placeholder="••••••••"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none transition"
                required
              />
            </div>
            <button
              type="submit"
              className="w-full bg-gradient-to-r from-green-600 to-emerald-600 text-white py-3 rounded-lg font-medium hover:from-green-700 hover:to-emerald-700 transition transform hover:scale-[1.02] shadow-md"
            >
              Créer mon village résistant
            </button>
          </form>

          <div className="mt-6 text-center space-y-3">
            <button
              onClick={() => setCurrentPage('login')}
              className="text-green-600 hover:text-green-700 font-medium text-sm block w-full"
            >
              Déjà résistant ? Se connecter
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const Dashboard = () => {
    const [activeTab, setActiveTab] = useState('home');
    const [showResources, setShowResources] = useState(false);

    const ProgressBar = ({ value, color, bgColor = 'bg-gray-200' }) => (
      <div className={`w-full ${bgColor} rounded-full h-3 overflow-hidden`}>
        <div
          className={`h-full ${color} transition-all duration-500 rounded-full`}
          style={{ width: `${value}%` }}
        />
      </div>
    );

    const ResourceCard = ({ title, description, link, icon: Icon }) => (
      <a 
        href={link} 
        target="_blank" 
        rel="noopener noreferrer"
        className="block border border-gray-200 rounded-lg p-4 hover:border-green-300 hover:shadow-md transition"
      >
        <div className="flex items-start space-x-3">
          <div className="p-2 bg-green-100 rounded-lg">
            <Icon className="w-5 h-5 text-green-600" />
          </div>
          <div className="flex-1">
            <h4 className="font-medium text-gray-800">{title}</h4>
            <p className="text-sm text-gray-600 mt-1">{description}</p>
          </div>
        </div>
      </a>
    );

    return (
      <div className="min-h-screen bg-gradient-to-b from-green-50 to-white">
        {/* Header */}
        <header className="bg-white border-b border-green-200 sticky top-0 z-50 shadow-sm">
          <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="p-2 bg-green-100 rounded-lg">
                <Leaf className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-800">
                  <span className="text-green-600">NIRD</span> Builder
                </h1>
                <p className="text-sm text-gray-600 flex items-center">
                  <School className="w-3 h-3 mr-1" /> {schoolState.name}
                </p>
              </div>
            </div>
            
            <div className="flex items-center space-x-6">
              <div className="hidden md:flex items-center space-x-4">
                <div className="text-right">
                  <div className="text-sm text-gray-600">Niveau {schoolState.level}</div>
                  <div className="text-lg font-bold text-green-600">{schoolState.points} pts</div>
                </div>
                <div className="text-right">
                  <div className="text-sm text-gray-600">Membres actifs</div>
                  <div className="text-lg font-bold text-emerald-600">{schoolState.members} 👥</div>
                </div>
              </div>
              
              <div className="flex items-center space-x-3">
                <div className="text-right hidden sm:block">
                  <div className="text-sm font-medium text-gray-800">{user?.name}</div>
                  <div className="text-xs text-gray-500">{user?.role}</div>
                </div>
                <button
                  onClick={handleLogout}
                  className="p-2 hover:bg-gray-100 rounded-lg transition"
                  title="Déconnexion"
                >
                  <LogOut className="w-5 h-5 text-gray-600" />
                </button>
              </div>
            </div>
          </div>
        </header>

        {/* Navigation */}
        <nav className="bg-white border-b border-green-200">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex space-x-1 overflow-x-auto">
              {[
                { id: 'home', icon: Home, label: 'Village' },
                { id: 'actions', icon: Zap, label: 'Actions NIRD' },
                { id: 'community', icon: Users, label: 'Communauté' },
                { id: 'stats', icon: BarChart3, label: 'Progression' },
                { id: 'leaderboard', icon: Trophy, label: 'Classement' },
                { id: 'resources', icon: Download, label: 'Ressources' },
                { id: 'about', icon: Info, label: 'À propos' }
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-2 px-4 py-3 border-b-2 transition whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'border-green-600 text-green-600'
                      : 'border-transparent text-gray-600 hover:text-gray-800'
                  }`}
                >
                  <tab.icon className="w-4 h-4" />
                  <span className="font-medium text-sm">{tab.label}</span>
                </button>
              ))}
            </div>
          </div>
        </nav>

        {/* Main Content */}
        <main className="max-w-7xl mx-auto px-4 py-8">
          {activeTab === 'home' && (
            <div className="space-y-8">
              {/* Hero Section */}
              <div className="bg-gradient-to-r from-green-600 to-emerald-700 rounded-2xl p-8 text-white shadow-xl">
                <div className="max-w-2xl">
                  <h1 className="text-3xl font-bold mb-4">Bienvenue au <span className="text-yellow-300">Village Numérique Résistant</span></h1>
                  <p className="text-lg opacity-90 mb-6">
                    Face aux Goliath du numérique, devenez le David de votre établissement. 
                    Adoptez un numérique Inclusif, Responsable et Durable.
                  </p>
                  <div className="flex flex-wrap gap-4">
                    <button 
                      onClick={() => setActiveTab('actions')}
                      className="bg-white text-green-700 px-6 py-3 rounded-lg font-bold hover:bg-green-50 transition shadow-md"
                    >
                      🚀 Lancer une action
                    </button>
                    <button 
                      onClick={() => setShowResources(!showResources)}
                      className="bg-green-800 text-white px-6 py-3 rounded-lg font-bold hover:bg-green-900 transition border border-green-700"
                    >
                      📚 Voir les ressources
                    </button>
                  </div>
                </div>
              </div>

              {/* Stats Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-6 text-white shadow-lg">
                  <div className="flex items-center justify-between mb-4">
                    <Heart className="w-8 h-8 opacity-80" />
                    <span className="text-3xl font-bold">{schoolState.inclusion}%</span>
                  </div>
                  <h3 className="text-lg font-semibold mb-1">Inclusion</h3>
                  <p className="text-sm opacity-90">Numérique accessible à tous</p>
                  <ProgressBar value={schoolState.inclusion} color="bg-blue-300" />
                </div>

                <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-6 text-white shadow-lg">
                  <div className="flex items-center justify-between mb-4">
                    <Shield className="w-8 h-8 opacity-80" />
                    <span className="text-3xl font-bold">{schoolState.responsibility}%</span>
                  </div>
                  <h3 className="text-lg font-semibold mb-1">Responsabilité</h3>
                  <p className="text-sm opacity-90">Données et éthique</p>
                  <ProgressBar value={schoolState.responsibility} color="bg-purple-300" />
                </div>

                <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-6 text-white shadow-lg">
                  <div className="flex items-center justify-between mb-4">
                    <Leaf className="w-8 h-8 opacity-80" />
                    <span className="text-3xl font-bold">{schoolState.sustainability}%</span>
                  </div>
                  <h3 className="text-lg font-semibold mb-1">Durabilité</h3>
                  <p className="text-sm opacity-90">Sobriété numérique</p>
                  <ProgressBar value={schoolState.sustainability} color="bg-green-300" />
                </div>
              </div>

              {/* Recent Actions & Testimonials */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div className="bg-white rounded-xl shadow-md p-6">
                  <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                    <Zap className="w-5 h-5 mr-2 text-green-600" /> Dernières actions
                  </h2>
                  {schoolState.actions.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <Zap className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>Commencez votre transformation numérique !</p>
                      <button 
                        onClick={() => setActiveTab('actions')}
                        className="mt-4 text-green-600 hover:text-green-700 font-medium"
                      >
                        Voir les actions disponibles →
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {schoolState.actions.slice(-3).reverse().map((action, idx) => (
                        <div key={idx} className="flex items-center space-x-4 p-3 bg-green-50 rounded-lg">
                          <action.icon className="w-5 h-5 text-green-600" />
                          <div className="flex-1">
                            <h4 className="font-medium text-gray-800">{action.title}</h4>
                            <p className="text-sm text-gray-600">{action.desc}</p>
                          </div>
                          <div className="text-right">
                            <div className="text-sm font-medium text-green-600">+{action.rewards.points} pts</div>
                            <div className="text-xs text-gray-500">{new Date(action.timestamp).toLocaleDateString()}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="bg-white rounded-xl shadow-md p-6">
                  <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                    <Users className="w-5 h-5 mr-2 text-green-600" /> Témoignages
                  </h2>
                  <div className="space-y-4">
                    {testimonials.map((testimonial, idx) => (
                      <div key={idx} className="border-l-4 border-green-500 pl-4 py-2">
                        <p className="text-gray-700 italic">"{testimonial.text}"</p>
                        <div className="mt-2">
                          <div className="font-medium text-gray-800">{testimonial.name}</div>
                          <div className="text-sm text-gray-600">{testimonial.role} • {testimonial.school}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'actions' && (
            <div className="space-y-8">
              <div className="bg-white rounded-xl shadow-md p-6">
                <div className="flex flex-col md:flex-row md:items-center justify-between mb-8">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-800">Actions NIRD disponibles</h2>
                    <p className="text-gray-600">Transformez votre établissement en village numérique résistant</p>
                  </div>
                  <div className="mt-4 md:mt-0 bg-green-50 text-green-800 px-4 py-2 rounded-lg">
                    <div className="font-bold text-lg">{schoolState.points} points disponibles</div>
                    <div className="text-sm">Pour réaliser des actions</div>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {nirdActions.map(action => {
                    const Icon = action.icon;
                    return (
                      <div key={action.id} className="border border-gray-200 rounded-xl p-6 hover:shadow-lg transition hover:border-green-300">
                        <div className="flex items-start justify-between mb-4">
                          <div className="p-3 bg-green-100 rounded-lg">
                            <Icon className="w-6 h-6 text-green-600" />
                          </div>
                          <div className="flex flex-col items-end">
                            <span className={`px-3 py-1 rounded-full text-xs font-medium mb-2 ${
                              action.difficulty === 'Facile' ? 'bg-green-100 text-green-700' :
                              action.difficulty === 'Moyen' ? 'bg-yellow-100 text-yellow-700' :
                              'bg-red-100 text-red-700'
                            }`}>
                              {action.difficulty}
                            </span>
                            <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                              {action.category}
                            </span>
                          </div>
                        </div>
                        
                        <h3 className="text-lg font-bold text-gray-800 mb-2">{action.title}</h3>
                        <p className="text-gray-600 text-sm mb-4">{action.desc}</p>
                        
                        <div className="space-y-3 mb-4">
                          <div className="flex items-center text-sm text-gray-600">
                            <span className="font-medium mr-2">⏱️ Durée:</span>
                            <span>{action.duration}</span>
                          </div>
                          <div className="grid grid-cols-3 gap-2 text-center text-xs">
                            <div className="bg-blue-50 text-blue-700 p-2 rounded">
                              +{action.rewards.inclusion}% Inclusion
                            </div>
                            <div className="bg-purple-50 text-purple-700 p-2 rounded">
                              +{action.rewards.responsibility}% Responsabilité
                            </div>
                            <div className="bg-green-50 text-green-700 p-2 rounded">
                              +{action.rewards.sustainability}% Durabilité
                            </div>
                          </div>
                        </div>
                        
                        <button
                          onClick={() => applyAction(action)}
                          disabled={schoolState.points < action.cost}
                          className={`w-full py-3 rounded-lg font-medium transition ${
                            schoolState.points >= action.cost
                              ? 'bg-gradient-to-r from-green-600 to-emerald-600 text-white hover:from-green-700 hover:to-emerald-700 shadow-md'
                              : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                          }`}
                        >
                          {schoolState.points >= action.cost
                            ? `Réaliser cette action (${action.cost} pts)`
                            : `${action.cost - schoolState.points} points manquants`}
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'community' && (
            <div className="space-y-6">
              <div className="bg-white rounded-xl shadow-md p-8">
                <h2 className="text-2xl font-bold text-gray-800 mb-6">La communauté NIRD</h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-700 mb-4">Les acteurs du projet</h3>
                    <div className="space-y-3">
                      {['Élèves et éco-délégués', 'Enseignants et enseignantes', 'Directions d\'établissements', 
                        'Techniciens et administrateurs réseaux', 'Associations partenaires', 'Collectivités territoriales',
                        'Services académiques et ministère'].map((actor, idx) => (
                        <div key={idx} className="flex items-center space-x-3 p-3 bg-green-50 rounded-lg">
                          <Users className="w-5 h-5 text-green-600" />
                          <span className="text-gray-700">{actor}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  <div>
                    <h3 className="text-lg font-semibold text-gray-700 mb-4">Votre établissement</h3>
                    <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl p-6 text-white">
                      <div className="text-center mb-4">
                        <School className="w-12 h-12 mx-auto mb-3" />
                        <h4 className="text-xl font-bold">{schoolState.name}</h4>
                        <p className="opacity-90">Village résistant de niveau {schoolState.level}</p>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4 mt-6">
                        <div className="text-center">
                          <div className="text-3xl font-bold">{schoolState.members}</div>
                          <div className="text-sm opacity-90">Membres actifs</div>
                        </div>
                        <div className="text-center">
                          <div className="text-3xl font-bold">{schoolState.actions.length}</div>
                          <div className="text-sm opacity-90">Actions réalisées</div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                      <h4 className="font-bold text-blue-800 mb-2">💡 Prochaine étape recommandée</h4>
                      <p className="text-sm text-blue-700">
                        Organisez une réunion avec les éco-délégués pour planifier un repair café numérique.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'stats' && (
            <div className="space-y-6">
              <div className="bg-white rounded-xl shadow-md p-6">
                <h2 className="text-2xl font-bold text-gray-800 mb-6">Statistiques de progression</h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                  <div className="border border-gray-200 rounded-lg p-6">
                    <h3 className="font-semibold text-gray-700 mb-4">Progression générale</h3>
                    <div className="space-y-4">
                      <div>
                        <div className="flex justify-between mb-2">
                          <span className="text-sm font-medium text-gray-700">Niveau actuel</span>
                          <span className="text-sm font-bold text-green-600">Niveau {schoolState.level}</span>
                        </div>
                        <ProgressBar value={(schoolState.points % 500) / 5} color="bg-gradient-to-r from-green-500 to-emerald-500" />
                        <p className="text-xs text-gray-500 mt-1">{schoolState.points % 500} / 500 pts vers niveau {schoolState.level + 1}</p>
                      </div>
                      <div>
                        <div className="flex justify-between mb-2">
                          <span className="text-sm font-medium text-gray-700">Score NIRD global</span>
                          <span className="text-sm font-bold text-gray-800">
                            {Math.round((schoolState.inclusion + schoolState.responsibility + schoolState.sustainability) / 3)}%
                          </span>
                        </div>
                        <ProgressBar 
                          value={(schoolState.inclusion + schoolState.responsibility + schoolState.sustainability) / 3} 
                          color="bg-gradient-to-r from-blue-500 via-purple-500 to-green-500" 
                        />
                      </div>
                    </div>
                  </div>
                  
                  <div className="border border-gray-200 rounded-lg p-6">
                    <h3 className="font-semibold text-gray-700 mb-4">Impact réalisé</h3>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between p-3 bg-green-50 rounded">
                        <span className="text-gray-700">Ordinateurs reconditionnés</span>
                        <span className="font-bold text-green-600">{schoolState.actions.filter(a => a.id === 1).length * 10}</span>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-blue-50 rounded">
                        <span className="text-gray-700">Licences économisées</span>
                        <span className="font-bold text-blue-600">{schoolState.actions.filter(a => [1, 4].includes(a.id)).length * 15}K€</span>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-purple-50 rounded">
                        <span className="text-gray-700">Membres formés</span>
                        <span className="font-bold text-purple-600">{schoolState.members}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="border-t pt-6">
                  <h3 className="font-semibold text-gray-700 mb-4">Détail des piliers NIRD</h3>
                  <div className="space-y-4">
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center space-x-2">
                          <Heart className="w-5 h-5 text-blue-600" />
                          <span className="font-medium text-gray-700">Inclusion</span>
                        </div>
                        <span className="text-lg font-bold text-blue-600">{schoolState.inclusion}%</span>
                      </div>
                      <ProgressBar value={schoolState.inclusion} color="bg-blue-600" />
                      <p className="text-xs text-gray-500 mt-1">Accessibilité numérique pour tous, sans exclusion technique ou économique</p>
                    </div>
                    
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center space-x-2">
                          <Shield className="w-5 h-5 text-purple-600" />
                          <span className="font-medium text-gray-700">Responsabilité</span>
                        </div>
                        <span className="text-lg font-bold text-purple-600">{schoolState.responsibility}%</span>
                      </div>
                      <ProgressBar value={schoolState.responsibility} color="bg-purple-600" />
                      <p className="text-xs text-gray-500 mt-1">Protection des données, souveraineté numérique et éthique</p>
                    </div>
                    
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center space-x-2">
                          <Leaf className="w-5 h-5 text-green-600" />
                          <span className="font-medium text-gray-700">Durabilité</span>
                        </div>
                        <span className="text-lg font-bold text-green-600">{schoolState.sustainability}%</span>
                      </div>
                      <ProgressBar value={schoolState.sustainability} color="bg-green-600" />
                      <p className="text-xs text-gray-500 mt-1">Réemploi du matériel, sobriété numérique, impact écologique réduit</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'leaderboard' && (
            <div className="bg-white rounded-xl shadow-md p-6">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">Classement national des villages résistants</h2>
              
              <div className="space-y-3 mb-8">
                {leaderboard.slice(0, 5).map((entry) => (
                  <div key={entry.rank} className={`flex items-center justify-between p-4 rounded-lg border-2 transition ${
                    entry.rank === 1 ? 'bg-gradient-to-r from-yellow-50 to-yellow-100 border-yellow-400' :
                    entry.rank === 2 ? 'bg-gradient-to-r from-gray-50 to-gray-100 border-gray-300' :
                    entry.rank === 3 ? 'bg-gradient-to-r from-orange-50 to-orange-100 border-orange-300' :
                    'bg-white border-gray-200'
                  }`}>
                    <div className="flex items-center space-x-4">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                        entry.rank === 1 ? 'bg-yellow-400 text-yellow-900' :
                        entry.rank === 2 ? 'bg-gray-400 text-gray-900' :
                        entry.rank === 3 ? 'bg-orange-400 text-orange-900' :
                        'bg-gray-200 text-gray-700'
                      }`}>
                        {entry.rank}
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-800">{entry.school}</h3>
                        <p className="text-sm text-gray-600">{entry.region} • Niveau {entry.level}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-xl font-bold text-green-600">{entry.points.toLocaleString()}</div>
                      <div className="text-sm text-gray-600">points de résistance</div>
                    </div>
                  </div>
                ))}
                
                {/* Notre école */}
                <div className="flex items-center justify-between p-4 rounded-lg bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-300">
                  <div className="flex items-center space-x-4">
                    <div className="w-10 h-10 rounded-full bg-green-600 text-white flex items-center justify-center font-bold">
                      {leaderboard.findIndex(s => s.school === schoolState.name) + 1 || '?'}
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-800">{schoolState.name}</h3>
                      <p className="text-sm text-gray-600">Votre village • Niveau {schoolState.level}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-xl font-bold text-green-600">{schoolState.points.toLocaleString()}</div>
                    <div className="text-sm text-gray-600">votre score</div>
                  </div>
                </div>
              </div>
              
              <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                <h3 className="font-bold text-green-800 mb-2">🏆 Objectif du classement</h3>
                <p className="text-sm text-green-700">
                  Ce classement récompense les établissements les plus engagés dans la transition numérique responsable. 
                  Plus vous réalisez d'actions NIRD, plus vous montez dans le classement !
                </p>
              </div>
            </div>
          )}

          {activeTab === 'resources' && (
            <div className="space-y-6">
              <div className="bg-white rounded-xl shadow-md p-8">
                <h2 className="text-2xl font-bold text-gray-800 mb-6">Ressources NIRD</h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
                  <ResourceCard
                    title="Site officiel NIRD"
                    description="La plateforme principale du projet"
                    link="https://nird.forge.apps.education.fr/"
                    icon={Globe}
                  />
                  <ResourceCard
                    title="Forge des communs"
                    description="Ressources et outils libres"
                    link="https://forge.apps.education.fr/"
                    icon={GitBranch}
                  />
                  <ResourceCard
                    title="Guide Linux éducatif"
                    description="Tutoriels pour l'installation"
                    link="https://nird.forge.apps.education.fr/ressources"
                    icon={Laptop}
                  />
                  <ResourceCard
                    title="Charte de sobriété"
                    description="Modèle pour votre établissement"
                    link="https://nird.forge.apps.education.fr/ressources"
                    icon={FileText}
                  />
                  <ResourceCard
                    title="Reportage France 3"
                    description="Windows 11 : l'alternative des logiciels libres"
                    link="https://video.echirolles.fr/w/hVykGUtRZqRenBeiutqRvQ"
                    icon={AwardIcon}
                  />
                  <ResourceCard
                    title="Grand reportage France Inter"
                    description="Mises à jour de Windows et obsolescence"
                    link="https://www.radiofrance.fr/francéniter/podcasts/le-grand-reportage-de-france-inter"
                    icon={Coffee}
                  />
                </div>

                <div className="border-t pt-6">
                  <h3 className="text-lg font-semibold text-gray-700 mb-4">📱 Applications recommandées</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="border border-gray-200 rounded-lg p-4">
                      <div className="font-medium text-gray-800 mb-2">💻 Systèmes d'exploitation</div>
                      <ul className="text-sm text-gray-600 space-y-1">
                        <li>• Ubuntu Éducation</li>
                        <li>• Debian Édu</li>
                        <li>• Linux Mint</li>
                      </ul>
                    </div>
                    <div className="border border-gray-200 rounded-lg p-4">
                      <div className="font-medium text-gray-800 mb-2">🛠️ Outils bureautiques</div>
                      <ul className="text-sm text-gray-600 space-y-1">
                        <li>• LibreOffice</li>
                        <li>• OnlyOffice</li>
                        <li>• NextCloud</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'about' && (
            <div className="space-y-6">
              <div className="bg-white rounded-xl shadow-md p-8">
                <h2 className="text-3xl font-bold text-gray-800 mb-4">À propos de NIRD Builder</h2>
                
                <div className="mb-6">
                  <div className="inline-block bg-gradient-to-r from-green-600 to-emerald-600 text-white px-4 py-2 rounded-full text-sm font-bold mb-4">
                    Projet Nuit de l'Info 2025
                  </div>
                </div>
                
                <div className="prose max-w-none">
                  <p className="text-gray-700 leading-relaxed mb-4 text-lg">
                    <span className="font-bold text-green-700">NIRD Builder</span> est l'outil ludique qui transforme votre établissement en 
                    <span className="font-bold text-green-700"> village numérique résistant</span>. Inspiré par le collectif NIRD, ce projet est né pendant la Nuit de l'Info 2025.
                  </p>
                  
                  <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-6 my-6 border border-green-200">
                    <h3 className="text-xl font-bold text-green-800 mb-3">🎯 Pourquoi ce projet existe ?</h3>
                    <p className="text-gray-700">
                      Face à la fin du support de Windows 10 et la dépendance structurelle aux Big Tech, les établissements scolaires se retrouvent 
                      prisonnières d'un système qui impose <span className="font-bold">l'obsolescence programmée</span>, des 
                      <span className="font-bold"> licences coûteuses</span> et le <span className="font-bold">stockage de données hors UE</span>. 
                      NIRD propose une alternative concrète, progressive et collective.
                    </p>
                  </div>

                  <h3 className="text-xl font-bold text-gray-800 mt-8 mb-3">🎮 Comment ça marche ?</h3>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    Vous incarnez un établissement scolaire qui veut reprendre le contrôle de son numérique. Chaque action que vous 
                    entreprenez améliore vos trois piliers : <span className="font-bold text-blue-600">Inclusion</span> (accessibilité), 
                    <span className="font-bold text-purple-600"> Responsabilité</span> (données et éthique) et 
                    <span className="font-bold text-green-600"> Durabilité</span> (sobriété et réemploi). Plus vous agissez, plus vous gagnez de points et montez dans le classement national.
                  </p>

                  <h3 className="text-xl font-bold text-gray-800 mt-8 mb-4">✨ Les trois piliers NIRD</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                    <div className="bg-gradient-to-b from-blue-50 to-white rounded-lg p-5 border-2 border-blue-200">
                      <Heart className="w-10 h-10 text-blue-600 mb-3" />
                      <h4 className="font-bold text-blue-900 text-lg mb-2">Inclusion</h4>
                      <p className="text-sm text-blue-800">
                        Un numérique accessible à tous, sans exclusion technique ou économique. 
                        Logiciels libres, matériel reconditionné, formation pour tous.
                      </p>
                    </div>
                    <div className="bg-gradient-to-b from-purple-50 to-white rounded-lg p-5 border-2 border-purple-200">
                      <Shield className="w-10 h-10 text-purple-600 mb-3" />
                      <h4 className="font-bold text-purple-900 text-lg mb-2">Responsabilité</h4>
                      <p className="text-sm text-purple-800">
                        Protection des données, souveraineté numérique et éthique. 
                        Cloud local, contrôle des données, transparence.
                      </p>
                    </div>
                    <div className="bg-gradient-to-b from-green-50 to-white rounded-lg p-5 border-2 border-green-200">
                      <Leaf className="w-10 h-10 text-green-600 mb-3" />
                      <h4 className="font-bold text-green-900 text-lg mb-2">Durabilité</h4>
                      <p className="text-sm text-green-800">
                        Réemploi du matériel, sobriété numérique, impact écologique réduit. 
                        Repair cafés, économie circulaire, low-tech.
                      </p>
                    </div>
                  </div>

                  <div className="bg-gradient-to-r from-green-600 to-emerald-700 rounded-xl p-6 mt-8 text-white">
                    <h3 className="text-xl font-bold mb-3">🏫 Le projet NIRD</h3>
                    <p className="mb-4">
                      NIRD (Numérique Inclusif, Responsable et Durable) est une initiative portée par un collectif enseignant 
                      de la Forge des communs numériques éducatifs, projet soutenu par la Direction du numérique pour l'éducation.
                    </p>
                    <p>
                      Né au <span className="font-bold">lycée Carnot de Bruay-la-Buissière</span> (Hauts-de-France), 
                      le projet cherche à étendre cette démarche au maximum d'établissements scolaires.
                    </p>
                  </div>

                  <div className="mt-8">
                    <h4 className="font-bold text-gray-800 mb-3">📊 Chiffres clés (exemple)</h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="text-center p-4 bg-green-50 rounded-lg">
                        <div className="text-2xl font-bold text-green-600">20+</div>
                        <div className="text-sm text-gray-600">Établissements engagés</div>
                      </div>
                      <div className="text-center p-4 bg-blue-50 rounded-lg">
                        <div className="text-2xl font-bold text-blue-600">500+</div>
                        <div className="text-sm text-gray-600">Ordinateurs reconditionnés</div>
                      </div>
                      <div className="text-center p-4 bg-purple-50 rounded-lg">
                        <div className="text-2xl font-bold text-purple-600">150K€</div>
                        <div className="text-sm text-gray-600">Économies de licences</div>
                      </div>
                      <div className="text-center p-4 bg-yellow-50 rounded-lg">
                        <div className="text-2xl font-bold text-yellow-600">1000+</div>
                        <div className="text-sm text-gray-600">Personnes formées</div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-6 mt-8 border border-gray-200">
                    <p className="text-sm text-gray-600 text-center">
                      Projet créé lors de la Nuit de l'Info 2025<br/>
                      Porté par le collectif NIRD et le Bureau de la Nuit de l'Info<br/>
                      <span className="font-medium">Licence libre - Données hébergées en France</span>
                    </p>
                    <div className="flex justify-center space-x-4 mt-4">
                      <a 
                        href="https://nird.forge.apps.education.fr/" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-green-600 hover:text-green-700 text-sm font-medium"
                      >
                        Site officiel
                      </a>
                      <a 
                        href="https://forge.apps.education.fr/" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-green-600 hover:text-green-700 text-sm font-medium"
                      >
                        La Forge
                      </a>
                      <a 
                        href="https://www.cafepedagogique.net/2025/04/27/bruay-labuissiere-voyage-au-centre-du-libre-educatif/" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-green-600 hover:text-green-700 text-sm font-medium"
                      >
                        Article Café Pédagogique
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </main>

        {/* Footer */}
        <footer className="bg-gradient-to-r from-green-800 to-emerald-900 text-white py-8 mt-12">
          <div className="max-w-7xl mx-auto px-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div>
                <h3 className="text-lg font-bold mb-4 flex items-center">
                  <Leaf className="w-5 h-5 mr-2" /> NIRD Builder
                </h3>
                <p className="text-sm opacity-90">
                  Village Numérique Résistant<br/>
                  Projet Nuit de l'Info 2025<br/>
                  Numérique Inclusif, Responsable et Durable
                </p>
              </div>
              <div>
                <h3 className="text-lg font-bold mb-4">Partenaires</h3>
                <ul className="text-sm space-y-2 opacity-90">
                  <li>• Collectif enseignant NIRD</li>
                  <li>• Bureau de la Nuit de l'Info</li>
                  <li>• Direction du numérique pour l'éducation</li>
                  <li>• La Forge des communs</li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-bold mb-4">Licence</h3>
                <p className="text-sm opacity-90">
                  Ce projet est sous licence libre.<br/>
                  Code source disponible sur demande.<br/>
                  Utilisez, modifiez, partagez librement.
                </p>
              </div>
            </div>
            <div className="border-t border-green-700 mt-8 pt-6 text-center text-sm opacity-80">
              <p>© 2025 NIRD Builder - Tous droits partagés - Nuit de l'Info 2025</p>
            </div>
          </div>
        </footer>
      </div>
    );
  };

  return (
    <>
      {currentPage === 'login' && <LoginPage />}
      {currentPage === 'signup' && <SignupPage />}
      {currentPage === 'dashboard' && user && <Dashboard />}
    </>
  );
};

export default NirdBuilderApp;
